import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Plus, Trash2, Calculator, X, CheckCircle2, Zap, Lock } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { FadeUp, Eyebrow } from "./motion";
import PhoneInput from "./PhoneInput";
import { API, FALLBACK_FABRICS, MOTOR_COST, MIN_ORDER_TOTAL, scrollToId } from "../lib/site";
import { getUtm } from "../lib/utm";

let nextId = 1;
const newWindow = () => ({ id: nextId++, width: "", height: "", fabric_id: "sheer", motorized: false });

export default function Estimator() {
  const navigate = useNavigate();
  const goToBooking = () => {
    if (document.getElementById("book")) scrollToId("book");
    else navigate("/book-free-visit");
  };
  const [fabrics, setFabrics] = useState(FALLBACK_FABRICS);
  const [motorCost, setMotorCost] = useState(MOTOR_COST);
  const [minOrder, setMinOrder] = useState(MIN_ORDER_TOTAL);
  const [windows, setWindows] = useState([newWindow()]);
  const [leadOpen, setLeadOpen] = useState(false);
  const [lead, setLead] = useState({ name: "", whatsapp: "+971 ", email: "" });
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [unlocked, setUnlocked] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/fabrics`)
      .then((res) => {
        if (res.data?.fabrics?.length) setFabrics(res.data.fabrics);
        if (res.data?.motor_cost) setMotorCost(res.data.motor_cost);
        if (res.data?.min_order_total) setMinOrder(res.data.min_order_total);
      })
      .catch(() => {});
  }, []);

  const fabricMap = useMemo(() => Object.fromEntries(fabrics.map((f) => [f.id, f])), [fabrics]);

  const priceOf = (w) => {
    const width = parseInt(w.width);
    const height = parseInt(w.height);
    if (!width || !height) return 0;
    const rate = fabricMap[w.fabric_id]?.rate || 0;
    const sqm = (width / 100) * (height / 100);
    return Math.round(sqm * rate + (w.motorized ? motorCost : 0));
  };

  const rawTotal = windows.reduce((s, w) => s + priceOf(w), 0);
  const minApplied = rawTotal > 0 && rawTotal < minOrder;
  const total = minApplied ? minOrder : rawTotal;

  const update = (id, patch) =>
    setWindows((ws) => ws.map((w) => (w.id === id ? { ...w, ...patch } : w)));
  const addWindow = () => setWindows((ws) => [...ws, newWindow()]);
  const removeWindow = (id) =>
    setWindows((ws) => (ws.length > 1 ? ws.filter((w) => w.id !== id) : ws));

  const validWindows = windows.filter((w) => parseInt(w.width) > 0 && parseInt(w.height) > 0);

  const submitLead = async (e) => {
    e.preventDefault();
    if (!lead.name.trim() || lead.whatsapp.replace(/\D/g, "").length < 12) {
      toast.error("Please enter your name and WhatsApp number with country code (+971 …).");
      return;
    }
    if (!validWindows.length) {
      toast.error("Add dimensions for at least one window.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/leads`, {
        name: lead.name.trim(),
        whatsapp: lead.whatsapp.trim(),
        email: lead.email.trim() || undefined,
        windows: validWindows.map((w) => ({
          width: parseInt(w.width),
          height: parseInt(w.height),
          fabric_id: w.fabric_id,
          motorized: w.motorized,
        })),
        ...(getUtm() ? { utm: getUtm() } : {}),
      });
      setConfirmed(res.data);
      setUnlocked(true);
      toast.success("Estimate unlocked!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="estimator" data-testid="estimator-section" className="py-20 sm:py-28 bg-sand-soft/60">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
        <FadeUp>
          <Eyebrow className="mb-4">Instant pricing</Eyebrow>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-slate-900 max-w-2xl">
            Know your price <span className="italic text-reef">before we ring the bell.</span>
          </h2>
          <p className="mt-5 max-w-xl text-base sm:text-lg text-slate-600 leading-relaxed">
            Enter your window sizes, pick a fabric — get an approximate AED price instantly.
          </p>
        </FadeUp>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7 space-y-5">
            <AnimatePresence initial={false}>
              {windows.map((w, idx) => (
                <motion.div
                  key={w.id}
                  layout
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.97 }}
                  transition={{ duration: 0.35 }}
                  data-testid={`estimator-window-card-${idx}`}
                  className="bg-white border border-sand-border rounded-2xl p-6"
                >
                  <div className="flex items-center justify-between mb-5">
                    <div className="flex items-center gap-3">
                      <span className="w-9 h-9 rounded-full bg-reef text-sand-bg font-mono text-sm flex items-center justify-center">
                        {idx + 1}
                      </span>
                      <div>
                        <p className="font-serif text-lg text-slate-900 leading-none">Window {idx + 1}</p>
                        <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-slate-500 mt-1">
                          {fabricMap[w.fabric_id]?.name} — {w.width && w.height ? `${w.width}×${w.height} cm` : "enter dimensions"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-sm text-reef font-semibold" data-testid={`estimator-window-price-${idx}`}>
                        {unlocked ? `${priceOf(w).toLocaleString()} AED` : "•••"}
                      </span>
                      {windows.length > 1 && (
                        <button
                          data-testid={`estimator-remove-window-${idx}`}
                          onClick={() => removeWindow(w.id)}
                          className="text-slate-400 hover:text-red-600 transition-colors p-1"
                          aria-label="Remove window"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="font-mono text-[10px] tracking-[0.18em] uppercase text-slate-500">Width (cm)</label>
                      <input
                        type="number"
                        min="1"
                        max="2000"
                        placeholder="250"
                        value={w.width}
                        data-testid={`estimator-width-input-${idx}`}
                        onChange={(e) => update(w.id, { width: e.target.value })}
                        className="mt-1.5 w-full rounded-xl border border-sand-border bg-sand-bg/50 px-4 py-3 text-slate-900 focus:outline-none focus:ring-2 focus:ring-reef/40 focus:border-reef transition"
                      />
                    </div>
                    <div>
                      <label className="font-mono text-[10px] tracking-[0.18em] uppercase text-slate-500">Height (cm)</label>
                      <input
                        type="number"
                        min="1"
                        max="2000"
                        placeholder="260"
                        value={w.height}
                        data-testid={`estimator-height-input-${idx}`}
                        onChange={(e) => update(w.id, { height: e.target.value })}
                        className="mt-1.5 w-full rounded-xl border border-sand-border bg-sand-bg/50 px-4 py-3 text-slate-900 focus:outline-none focus:ring-2 focus:ring-reef/40 focus:border-reef transition"
                      />
                    </div>
                  </div>

                  <p className="mt-5 mb-2.5 font-mono text-[10px] tracking-[0.18em] uppercase text-slate-500">Fabric type</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid={`estimator-fabric-select-${idx}`}>
                    {fabrics.map((f) => (
                      <button
                        key={f.id}
                        type="button"
                        data-testid={`estimator-fabric-${f.id}-${idx}`}
                        onClick={() => update(w.id, { fabric_id: f.id })}
                        className={`rounded-xl border px-3 py-2.5 text-left transition-all duration-200 ${
                          w.fabric_id === f.id
                            ? "border-reef bg-reef-light"
                            : "border-sand-border hover:border-reef/40"
                        }`}
                      >
                        <p className={`text-sm font-medium leading-tight ${w.fabric_id === f.id ? "text-reef" : "text-slate-700"}`}>
                          {f.name}
                        </p>
                      </button>
                    ))}
                  </div>

                  <button
                    type="button"
                    data-testid={`estimator-motorized-toggle-${idx}`}
                    onClick={() => update(w.id, { motorized: !w.motorized })}
                    className={`mt-4 flex items-center gap-2.5 rounded-full border px-4 py-2 font-mono text-[11px] tracking-[0.1em] uppercase transition-all duration-200 ${
                      w.motorized ? "border-reef bg-reef text-sand-bg" : "border-sand-border text-slate-600 hover:border-reef/40"
                    }`}
                  >
                    <Zap className="w-3.5 h-3.5" />
                    Motorized +{motorCost} AED onwards
                  </button>
                </motion.div>
              ))}
            </AnimatePresence>

            <button
              data-testid="estimator-add-window-btn"
              onClick={addWindow}
              className="w-full rounded-2xl border border-dashed border-reef/40 py-4 font-mono text-xs tracking-[0.18em] uppercase text-reef hover:bg-reef-light transition-colors duration-200 flex items-center justify-center gap-2"
            >
              <Plus className="w-4 h-4" /> Add another window
            </button>
          </div>

          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-24 bg-reef text-sand-bg rounded-3xl p-7 sm:p-9 shadow-[0_40px_80px_-30px_rgba(134,95,52,0.5)]">
              <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-sand-bg/70">Estimated total price</p>
              <p className="mt-3 flex items-baseline gap-2">
                <span data-testid="estimator-total-price" className="font-serif text-5xl sm:text-6xl leading-none">
                  {unlocked ? total.toLocaleString() : "•••"}
                </span>
                <span className="font-mono text-sm text-sand-bg/70">AED</span>
              </p>
              <p className="mt-2 font-mono text-[11px] text-sand-bg/70">
                Total for {windows.length} window{windows.length !== 1 ? "s" : ""}
              </p>
              {!unlocked && (
                <p className="mt-4 flex items-start gap-2 text-[12px] text-sand-bg/85 leading-relaxed bg-sand-bg/10 rounded-xl px-4 py-3">
                  <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  Enter your name &amp; WhatsApp number to reveal your instant estimate.
                </p>
              )}

              <div className="mt-6 space-y-2.5 border-t border-sand-bg/15 pt-5">
                {windows.map((w, i) => (
                  <div key={w.id} className="flex justify-between text-sm" data-testid={`estimator-breakdown-row-${i}`}>
                    <span className="text-sand-bg/80">
                      Window {i + 1} — {w.width && w.height ? `${w.width}×${w.height}cm` : "no dimensions yet"}
                      {w.motorized ? " · motorized" : ""}
                    </span>
                    <span className="font-mono">{unlocked ? `${priceOf(w).toLocaleString()} AED` : "•••"}</span>
                  </div>
                ))}
                {unlocked && minApplied && (
                  <p className="text-[11px] font-mono text-sand-bg/70 pt-1">Minimum order of {minOrder} AED applied.</p>
                )}
              </div>

              <button
                data-testid="estimator-calculate-btn"
                onClick={() => setLeadOpen(true)}
                className="mt-7 w-full rounded-full bg-sand-bg text-reef py-4 font-mono text-xs tracking-[0.18em] uppercase hover:bg-white transition-colors duration-300 flex items-center justify-center gap-2"
              >
                <Calculator className="w-4 h-4" />
                {unlocked ? "Get exact quote on WhatsApp" : "Reveal my estimate"}
              </button>
              <button
                data-testid="estimator-book-visit-link"
                onClick={goToBooking}
                className="mt-3 w-full rounded-full border border-sand-bg/30 py-4 font-mono text-xs tracking-[0.18em] uppercase text-sand-bg hover:bg-sand-bg/10 transition-colors duration-300"
              >
                Book free visit instead
              </button>
              <p className="mt-4 text-[11px] text-sand-bg/60 leading-relaxed">
                *Estimates are approximate. Minimum charge over total order applies. Final quotation
                after free on-site measurement.
              </p>
            </div>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {leadOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-reef-dark/50 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setLeadOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, y: 30, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.97 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              data-testid="estimator-lead-modal"
              className="bg-white rounded-3xl w-full max-w-md p-7 sm:p-8 relative"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                data-testid="estimator-lead-close-btn"
                onClick={() => setLeadOpen(false)}
                className="absolute top-5 right-5 text-slate-400 hover:text-slate-700"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>

              {confirmed ? (
                <div className="text-center py-6" data-testid="estimator-lead-success">
                  <CheckCircle2 className="w-14 h-14 text-reef mx-auto" strokeWidth={1.5} />
                  <h3 className="mt-4 font-serif text-2xl text-slate-900">Estimate received!</h3>
                  <p className="mt-2 text-sm text-slate-600">
                    Estimate: <span className="font-mono text-reef font-semibold">{confirmed.total?.toLocaleString()} AED</span>
                  </p>
                  <p className="mt-1.5 text-xs text-slate-500 italic leading-relaxed">
                    (Subject to change — can go lower or a little higher depending on the selection of fabric and actual measurements)
                  </p>
                  <p className="mt-3 text-sm text-slate-600">We'll WhatsApp you shortly with fabric samples & a free visit slot.</p>
                  <button
                    data-testid="estimator-lead-done-btn"
                    onClick={() => { setLeadOpen(false); setConfirmed(null); }}
                    className="mt-6 px-8 py-3 rounded-full bg-reef text-sand-bg font-mono text-xs tracking-[0.18em] uppercase hover:bg-reef-dark transition-colors"
                  >
                    Done
                  </button>
                </div>
              ) : (
                <>
                  <h3 className="font-serif text-2xl text-slate-900">Get your free estimate</h3>
                  <p className="mt-1.5 text-sm text-slate-600">
                    Share your details — we'll get back to you on WhatsApp.
                  </p>
                  <form onSubmit={submitLead} className="mt-6 space-y-4">
                    <div>
                      <label className="font-mono text-[10px] tracking-[0.18em] uppercase text-slate-500">Full name *</label>
                      <input
                        data-testid="estimator-lead-name-input"
                        value={lead.name}
                        onChange={(e) => setLead({ ...lead, name: e.target.value })}
                        placeholder="Ahmed Al Mansouri"
                        className="mt-1.5 w-full rounded-xl border border-sand-border bg-sand-bg/50 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-reef/40 focus:border-reef transition"
                        required
                      />
                    </div>
                    <div>
                      <label className="font-mono text-[10px] tracking-[0.18em] uppercase text-slate-500">WhatsApp number *</label>
                      <PhoneInput
                        testid="estimator-lead-whatsapp-input"
                        value={lead.whatsapp}
                        onChange={(v) => setLead({ ...lead, whatsapp: v })}
                        className="mt-1.5 w-full rounded-xl border border-sand-border bg-sand-bg/50 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-reef/40 focus:border-reef transition"
                        required
                      />
                    </div>
                    <div>
                      <label className="font-mono text-[10px] tracking-[0.18em] uppercase text-slate-500">Email (optional)</label>
                      <input
                        type="email"
                        data-testid="estimator-lead-email-input"
                        value={lead.email}
                        onChange={(e) => setLead({ ...lead, email: e.target.value })}
                        placeholder="you@example.com"
                        className="mt-1.5 w-full rounded-xl border border-sand-border bg-sand-bg/50 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-reef/40 focus:border-reef transition"
                      />
                    </div>
                    <button
                      type="submit"
                      data-testid="estimator-lead-submit-btn"
                      disabled={submitting}
                      className="w-full rounded-full bg-reef text-sand-bg py-4 font-mono text-xs tracking-[0.18em] uppercase hover:bg-reef-dark transition-colors disabled:opacity-60"
                    >
                      {submitting ? "Unlocking…" : "Reveal my estimate"}
                    </button>
                  </form>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
